import {
  batchUnarchiveEmails,
  getArchivedEmailIdsInDateRange,
  getPaginated,
  unarchiveEmail,
} from "@/services/ai";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { z } from "zod";
import moment from "moment";
import { ArchiveRestore, Loader2 } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationPrevious,
  PaginationLink,
  PaginationNext,
} from "@/components/ui/pagination";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/form_fields/DatePicker";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const PAGE_SIZE = 10;

const createInitialStartDate = () =>
  moment().subtract(7, "days").startOf("day").toDate();

const createInitialEndDate = () => moment().endOf("day").toDate();

const formatDateLabel = (date: Date) => moment(date).format("DD/MM/YYYY");

const getDateParams = (startDate?: Date, endDate?: Date) => {
  if (!startDate) {
    return {};
  }

  return {
    startDate: moment(startDate).startOf("day").toDate(),
    endDate: moment(endDate ?? startDate).endOf("day").toDate(),
  };
};

const getPaginationItems = (currentPage: number, pageCount: number) => {
  if (pageCount <= 7) {
    return Array.from({ length: pageCount }, (_, i) => i + 1);
  }

  const pages = new Set([1, pageCount, currentPage]);

  if (currentPage > 1) {
    pages.add(currentPage - 1);
  }

  if (currentPage < pageCount) {
    pages.add(currentPage + 1);
  }

  if (currentPage <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }

  if (currentPage >= pageCount - 2) {
    pages.add(pageCount - 3);
    pages.add(pageCount - 2);
    pages.add(pageCount - 1);
  }

  return Array.from(pages)
    .filter((page) => page >= 1 && page <= pageCount)
    .sort((a, b) => a - b)
    .reduce<(number | "ellipsis")[]>((items, page) => {
      const previousPage = items[items.length - 1];

      if (typeof previousPage === "number" && page - previousPage > 1) {
        items.push("ellipsis");
      }

      items.push(page);
      return items;
    }, []);
};

const EmailListSkeleton = () => (
  <div className="flex min-w-0 flex-col gap-4">
    <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-9 w-48" />
    </div>
    {Array.from({ length: 6 }, (_, index) => (
      <div
        key={index}
        className="flex min-w-0 flex-col gap-3 rounded-lg border p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Skeleton className="h-4 w-4 shrink-0" />
          <Skeleton className="h-6 w-24 shrink-0 rounded-full" />
          <Skeleton className="h-4 min-w-0 flex-1" />
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-8 w-8" />
        </div>
      </div>
    ))}
  </div>
);

type bookingErrorType =
  | "missingAlias"
  | "creationDate"
  | "cancellation"
  | "checkInOut"
  | "refMissing"
  | "priceMismatch"
  | "tooLong"
  | "late"
  | "unknown";

const BOOKING_ERROR_TYPES: bookingErrorType[] = [
  "missingAlias",
  "creationDate",
  "cancellation",
  "checkInOut",
  "refMissing",
  "priceMismatch",
  "tooLong",
  "late",
  "unknown",
];

type bookingEmailStatus = "pending" | "success" | "error";

export const parsedContentSchema = z.object({
  _createdAt: z.string(),
  collectionId: z.string(),
  lang: z.string(),
  owner: z.string(),
  originalPrice: z.number(),
  lockDate: z.string(),
  guests: z
    .object({
      firstName: z.string(),
      secondName: z.string(),
      email: z.string(),
    })
    .array(),
  payload: z.object({
    reference: z.string(),
    checkin: z.string(),
    checkout: z.string(),
    board: z.string().optional(),
    rooms: z
      .object({
        name: z.string(),
        guestsAdults: z.number(),
        guestsKids: z.number(),
        amenities: z.string(),
      })
      .array(),

    extra: z.string().optional(),
  }),
  bookedWithLoyaltyCode: z.string().optional(),
  reservationType: z.string(),
});

export type bookingEmailType = {
  _id: string;
  _createdAt: Date;
  emailId: string;
  encryptedContent: { headers: { subject: string }; text: string };
  hashedContent: string;
  step: number;
  status: bookingEmailStatus;
  errorType?: bookingErrorType;
  hotelAlias: string;
  encryptedParsedContent:
    | z.infer<typeof parsedContentSchema>
    | "No data"
    | "Ofuscated data";
  expiresAt: Date;
};

const ArchivedAi = () => {
  const [emails, setEmails] = useState<bookingEmailType[]>();
  const [emailsLoading, setEmailsLoading] = useState(true);
  const [emailsPage, setEmailsPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalEmails, setTotalEmails] = useState(0);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [lastSelectedId, setLastSelectedId] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);
  const [startDate, setStartDate] = useState<Date>(() =>
    createInitialStartDate()
  );
  const [endDate, setEndDate] = useState<Date>(() => createInitialEndDate());
  const [showErrorsOnly, setShowErrorsOnly] = useState(false);
  const [isUnarchiveAllDialogOpen, setIsUnarchiveAllDialogOpen] =
    useState(false);
  const [unarchiveAllLoading, setUnarchiveAllLoading] = useState(false);

  const [shiftHeld, setShiftHeld] = useState(false);

  const visibleEmailIds = useMemo(
    () => emails?.map((email) => email._id) ?? [],
    [emails]
  );

  const allVisibleSelected =
    visibleEmailIds.length > 0 &&
    visibleEmailIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleEmailIds.some((id) =>
    selectedIds.has(id)
  );

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate =
      someVisibleSelected && !allVisibleSelected;
  }, [allVisibleSelected, someVisibleSelected]);

  function downHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(true);
    }
  }

  function upHandler(e: KeyboardEvent) {
    if (e.key === "Shift") {
      setShiftHeld(false);
    }
  }

  function blurHandler() {
    setShiftHeld(false);
  }

  useEffect(() => {
    window.addEventListener("keydown", downHandler);
    window.addEventListener("keyup", upHandler);
    window.addEventListener("blur", blurHandler);
    return () => {
      window.removeEventListener("keydown", downHandler);
      window.removeEventListener("keyup", upHandler);
      window.removeEventListener("blur", blurHandler);
    };
  }, []);

  const fetchEmails = async (page: number) => {
    setEmailsLoading(true);
    try {
      const res = await getPaginated({
        page: page,
        size: PAGE_SIZE,
        ...getDateParams(startDate, endDate),
        errorTypes: showErrorsOnly ? BOOKING_ERROR_TYPES : undefined,
        labels: ["archived"],
      });
      setEmails(res.data);
      setEmailsPage(Math.max(1, Math.ceil(res.page || page)));
      setTotalEmails(res.total ?? 0);
      setTotalPages(Math.ceil((res.total ?? 0) / (res.size || PAGE_SIZE)));
    } catch {
      setEmails(undefined);
      setTotalEmails(0);
      setTotalPages(0);
    } finally {
      setSelectedIds(new Set());
      setLastSelectedId(null);
      setEmailsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmails(emailsPage);
  }, [emailsPage, showErrorsOnly]);

  const refreshData = () => {
    if (emailsPage === 1) {
      fetchEmails(1);
      return;
    }

    setEmailsPage(1);
  };

  const handleShowErrorsOnlyChange = (checked: boolean) => {
    setShowErrorsOnly(checked);

    if (emailsPage !== 1) {
      setEmailsPage(1);
    }
  };

  const unarchiveAllInDateRange = async () => {
    setUnarchiveAllLoading(true);
    try {
      const ids = await getArchivedEmailIdsInDateRange(
        getDateParams(startDate, endDate)
      );

      if (ids.length === 0) {
        alert("Nessuna email archiviata trovata in questo intervallo.");
        setIsUnarchiveAllDialogOpen(false);
        return;
      }

      await batchUnarchiveEmails(ids);
      setIsUnarchiveAllDialogOpen(false);
      refreshData();
    } catch (error) {
      alert(error);
    } finally {
      setUnarchiveAllLoading(false);
    }
  };

  const toggleEmailSelection = (
    emailId: string,
    checked: boolean,
    index: number
  ) => {
    setSelectedIds((previousSelectedIds) => {
      const nextSelectedIds = new Set(previousSelectedIds);
      const anchorIndex = lastSelectedId
        ? visibleEmailIds.indexOf(lastSelectedId)
        : -1;
      const rangeIds =
        shiftHeld && anchorIndex !== -1
          ? visibleEmailIds.slice(
              Math.min(anchorIndex, index),
              Math.max(anchorIndex, index) + 1
            )
          : [emailId];

      rangeIds.forEach((id) => {
        if (checked) {
          nextSelectedIds.add(id);
          return;
        }

        nextSelectedIds.delete(id);
      });

      return nextSelectedIds;
    });
    setLastSelectedId(emailId);
  };

  const toggleVisibleSelection = (checked: boolean) => {
    setSelectedIds((previousSelectedIds) => {
      const nextSelectedIds = new Set(previousSelectedIds);

      visibleEmailIds.forEach((id) => {
        if (checked) {
          nextSelectedIds.add(id);
          return;
        }

        nextSelectedIds.delete(id);
      });

      return nextSelectedIds;
    });
    setLastSelectedId(null);
  };

  const selectedCount = selectedIds.size;
  const paginationItems = getPaginationItems(emailsPage, totalPages);
  const isInitialEmailsLoading = emailsLoading && !emails;
  const isRefreshingEmails = emailsLoading && Boolean(emails);
  const isBusy = emailsLoading || unarchiveAllLoading;

  return (
    <div className="flex min-w-0 max-w-full flex-col gap-12 overflow-x-hidden">
      <div className="flex min-w-0 flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">
              Data inizio
            </span>
            <DatePicker
              value={startDate}
              onChange={(date) => {
                setStartDate(date);
                if (moment(date).isAfter(endDate, "day")) {
                  setEndDate(moment(date).endOf("day").toDate());
                }
              }}
              placeholder="Seleziona inizio"
              className="w-full sm:w-fit"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-sm text-muted-foreground">Data fine</span>
            <DatePicker
              value={endDate}
              onChange={(date) => {
                setEndDate(date);
                if (moment(date).isBefore(startDate, "day")) {
                  setStartDate(moment(date).startOf("day").toDate());
                }
              }}
              placeholder="Seleziona fine"
              className="w-full sm:w-fit"
            />
          </div>
          <Button
            variant="takyon"
            onClick={refreshData}
            disabled={!startDate || !endDate || isBusy}
            className="sm:mt-6"
          >
            {isBusy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Aggiorno
              </>
            ) : (
              "Aggiorna"
            )}
          </Button>
          <div className="flex items-center gap-2 sm:mt-6">
            <Switch
              checked={showErrorsOnly}
              onCheckedChange={handleShowErrorsOnlyChange}
              disabled={isBusy}
              aria-label="Mostra solo errori"
            />
            <span className="text-sm">Mostra errori</span>
          </div>
          <Button
            type="button"
            variant="destructive"
            className="w-fit gap-2 sm:mt-6"
            disabled={!startDate || !endDate || isBusy}
            onClick={() => setIsUnarchiveAllDialogOpen(true)}
          >
            <ArchiveRestore className="h-4 w-4" />
            Disarchivia tutto
          </Button>
        </div>
        <div>
          <Link to="/admin/ai">Indietro</Link>
        </div>
      </div>
      <p className="text-3xl">Archivio</p>
      {isInitialEmailsLoading ? (
        <EmailListSkeleton />
      ) : emails && emails.length > 0 ? (
        <>
          <div
            className={`flex min-w-0 flex-col gap-4 transition-opacity ${
              isRefreshingEmails ? "pointer-events-none opacity-60" : ""
            }`}
          >
            <div className="flex flex-col gap-3 rounded-lg border p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex gap-2 items-center">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  className="cursor-pointer"
                  disabled={isBusy}
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    toggleVisibleSelection(e.target.checked);
                  }}
                />
                <p>
                  Seleziona pagina
                  {selectedCount > 0 ? ` (${selectedCount})` : ""}
                </p>
              </div>

              <button
                className="flex w-fit items-center gap-2 rounded-lg border px-3 py-2 text-sm shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
                onClick={async (event) => {
                  event.stopPropagation();
                  if (
                    confirm("Clicca conferma per disarchiviare i selezionati")
                  ) {
                    await batchUnarchiveEmails(Array.from(selectedIds));
                    fetchEmails(emailsPage);
                  }
                }}
                disabled={selectedCount === 0 || isBusy}
                aria-busy={isBusy}
              >
                <ArchiveRestore className="h-4 w-4" />
                <span>Disarchiva selezionati</span>
              </button>
            </div>
            {emails.map((email, index) => (
              <div
                key={email._id}
                className="flex min-w-0 flex-col gap-3 rounded-lg border p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    disabled={isBusy}
                    checked={selectedIds.has(email._id)}
                    onChange={(e) => {
                      toggleEmailSelection(
                        email._id,
                        e.target.checked,
                        index
                      );
                    }}
                  />
                  <Link
                    className="flex min-w-0 flex-1 items-center gap-3"
                    to={`/admin/ai/${email._id}`}
                    target="_blank"
                  >
                    <div
                      className="shrink-0 rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        backgroundColor:
                          email.status === "success"
                            ? "#9AFEE3"
                            : email.status === "error"
                            ? "#ff746c"
                            : "#ddd",
                      }}
                    >
                      #
                      {email.status === "error"
                        ? email.errorType
                        : email.status}
                    </div>
                    <p className="truncate">
                      {email.encryptedContent.headers.subject}
                    </p>
                  </Link>
                </div>
                <div className="flex shrink-0 items-center justify-between gap-4 sm:justify-end">
                  <p className="text-xs text-gray-500 whitespace-nowrap">
                    {moment(email._createdAt).format("DD/MM/YYYY hh:mm:ss")}
                  </p>
                  <button
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (confirm("Clicca conferma per disarchiviare")) {
                        await unarchiveEmail(email._id);
                        fetchEmails(emailsPage);
                        return;
                      }
                    }}
                    className="rounded-md p-2 hover:bg-muted"
                    disabled={isBusy}
                    aria-label="Disarchivia email"
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div
            className={`flex min-w-0 flex-col items-center gap-3 transition-opacity sm:flex-row sm:justify-between ${
              isRefreshingEmails ? "opacity-60" : ""
            }`}
          >
            <p className="text-sm text-muted-foreground">
              {isRefreshingEmails
                ? "Aggiornamento..."
                : `Pagina ${emailsPage} di ${totalPages} - ${totalEmails} email`}
            </p>
            <Pagination className="mx-0 w-auto">
              <PaginationContent className="flex-wrap justify-center">
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    className={
                      emailsPage <= 1 || isBusy
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      setEmailsPage((p) => Math.max(p - 1, 1));
                    }}
                  />
                </PaginationItem>

                {paginationItems.map((item, index) =>
                  item === "ellipsis" ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <PaginationLink
                        href="#"
                        isActive={emailsPage === item}
                        onClick={(event) => {
                          event.preventDefault();
                          setEmailsPage(item);
                        }}
                      >
                        {item}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    className={
                      emailsPage >= totalPages || isBusy
                        ? "pointer-events-none opacity-50"
                        : ""
                    }
                    onClick={(event) => {
                      event.preventDefault();
                      setEmailsPage((p) => Math.min(p + 1, totalPages));
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </>
      ) : (
        <div className="rounded-lg border p-8 text-center text-muted-foreground">
          {showErrorsOnly
            ? "Nessuna email archiviata con errore trovata per i filtri selezionati."
            : "Nessuna email archiviata trovata per i filtri selezionati."}
        </div>
      )}
      <Dialog
        open={isUnarchiveAllDialogOpen}
        onOpenChange={(open) => {
          if (!unarchiveAllLoading) {
            setIsUnarchiveAllDialogOpen(open);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disarchiviare tutte le email?</DialogTitle>
            <DialogDescription>
              Questa azione disarchivierà tutte le email archiviate create tra{" "}
              {formatDateLabel(startDate)} e {formatDateLabel(endDate)}. La
              selezione delle righe non verrà considerata.
              {showErrorsOnly
                ? " Il filtro Mostra errori è attivo, ma questa azione userà comunque tutte le email archiviate nell'intervallo."
                : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg border bg-muted/40 p-3 text-sm">
            Dopo la conferma, le email passeranno dall'archivio alla inbox e
            spariranno da questa lista.
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={unarchiveAllLoading}
              onClick={() => setIsUnarchiveAllDialogOpen(false)}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={unarchiveAllLoading}
              onClick={unarchiveAllInDateRange}
            >
              {unarchiveAllLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Disarchivio
                </>
              ) : (
                "Conferma"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArchivedAi;
